import { useCallback, useEffect, useRef } from 'react';
import {
  FeedbackOptions,
  type CreatePlanWizardValues,
} from '@/packages/idp/pages/create-plan/types';
import { ProcessingLoading } from '@/packages/idp/pages/create-plan/components/processing-loading';
import {
  FeedbackSkillsStreamingEvent,
  useFeedbackSkillsStreaming,
} from '@/context/channels/guidance-feedback/streaming';
import { IdpEvent } from '@/constants/analytics';
import { useWizard } from '@/context/wizard';
import { useShowUserFeedback } from '@/data/guidance/feedback';
import { useUpdateProfileBasedRecommendation } from '@/data/idp/profile-based-recommendations';
import { RecommendationPlanIds } from '@/data/idp/profile-based-recommendations/interfaces';
import { useUserPrioritySkillSetsIndex } from '@/data/taxonomy/user-priority-skill-sets';
import { track } from '@/utils/analytics';

export const ProcessingFeedback = () => {
  const { actions, values } = useWizard<CreatePlanWizardValues>();
  const { userFeedback, refetch: refetchUserFeedback } = useShowUserFeedback();
  const { refetch: refetchSelectedSkills } = useUserPrioritySkillSetsIndex();
  const { mutate: updateRecommendation } = useUpdateProfileBasedRecommendation(
    values?.id || ''
  );
  const hasStartedRef = useRef(false);
  const hasNavigatedRef = useRef(false);

  // Falls back to the REST-cached current feedback (e.g. after a page refresh),
  // since the wizard-local feedbackId set right after creation doesn't survive it.
  const feedbackId =
    values[RecommendationPlanIds.ProcessingFeedback]?.feedbackId ??
    userFeedback?.id;

  const handleCompleted = useCallback(async () => {
    if (hasNavigatedRef.current) return;
    hasNavigatedRef.current = true;

    const { data: currentFeedback } = await refetchUserFeedback();
    await refetchSelectedSkills();

    updateRecommendation(
      { current_step: RecommendationPlanIds.SkillSelection },
      {
        onSuccess: () => {
          track(IdpEvent.WizardStepCompleted, {
            plan_id: values?.planId,
            step: RecommendationPlanIds.ProcessingFeedback,
          });
          actions.updateValues({
            [RecommendationPlanIds.Feedback]: {
              currentFeedback,
              option: FeedbackOptions.New,
            },
          });
          actions.navigate(RecommendationPlanIds.SkillSelection);
        },
      }
    );
  }, [
    actions,
    refetchSelectedSkills,
    refetchUserFeedback,
    updateRecommendation,
    values?.planId,
  ]);

  const { status, startStreaming } = useFeedbackSkillsStreaming({
    onCompleted: handleCompleted,
  });

  useEffect(() => {
    if (feedbackId && !hasStartedRef.current) {
      hasStartedRef.current = true;
      startStreaming(feedbackId);
    }
  }, [feedbackId, startStreaming]);

  const hasError = status === FeedbackSkillsStreamingEvent.Error;

  useEffect(() => {
    if (!hasError) return;

    track(IdpEvent.AiFailed, {
      plan_id: values?.planId,
      stage: 'feedback_processing',
    });
  }, [hasError, values?.planId]);

  const handleRetry = () => {
    updateRecommendation(
      { current_step: RecommendationPlanIds.NewFeedback },
      {
        onSuccess: () => {
          actions.navigate(RecommendationPlanIds.NewFeedback);
        },
      }
    );
  };

  if (hasError) {
    return (
      <ProcessingLoading
        title="Não conseguimos processar seu feedback."
        description="Ocorreu um erro ao processar seu feedback. Tente novamente mais tarde."
        button={{
          onClick: handleRetry,
          children: 'Tentar novamente',
          variant: 'contained',
        }}
      />
    );
  }

  return (
    <ProcessingLoading
      title="Mapeando habilidades..."
      description="Estamos analisando seu feedback para identificar as habilidades mais relevantes para o seu desenvolvimento."
    />
  );
};
